import { forwardRef, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

import styles from './Tabs.module.scss';

import { Badge } from '../../display/Badge';
import { Tag } from '../../display/Tag';
import { Link } from '../../navigation/Link';
import { TabsDropdown } from './TabsDropdown';

import type { TabsItemType, TabsSize, OnChooseItem } from './types';
import type { Props as BadgeProps } from '../../display/Badge/Badge';
import type { Ref } from 'react';

export type TabsRefElement = HTMLDivElement;

export type Props = {
  /** The `aria-current` value to apply to the selected item. */
  'aria-current'?: 'page' | 'step';
  /**
   * Optional label that provides context as to what the navigation is used for.
   * Used by screen reading software.
   */
  'aria-label'?: string;
  /**
   * React ref that is forwarded along as a prop to facilitate typing and to eliminate use of `forwardRef`.
   */
  forwardedRef?: Ref<TabsRefElement>;
  /** An array of items to render within the navigation. */
  items: Array<TabsItemType>;
  /** Callback for when an item is chosen. */
  onChooseItem?: OnChooseItem;
  /**
   * The `href` of the currently selected navigation item.
   * `href` is used rather than the full item since the item object could be cloned, which ruins `===` checks.
   */
  selectedItemHref: string;
  /**
   * Optional function that will determine if the nav item should be highlighted or not.
   * When not provided, the `href` will be compared to the `selectedItemHref` passed to `Tabs`.
   */
  isItemSelected?: (item: TabsItemType, selectedItemHref: string) => boolean;
  /**
   * Size of navigation.
   * `medium` typically used for small screens and `large` typically used for larger screens.
   */
  size?: TabsSize;
  /**
   * Whether or not the component should render the gray indicator track beneath the nav items.
   */
  shouldRenderTrack?: boolean;
  /* Determines the styling of the `Tabs` items. */
  type?: 'default' | 'brand';
};

type TabsItemRefElement = HTMLAnchorElement | HTMLDivElement;

type TabsItemProps = TabsItemType & {
  ariaCurrent?: 'page' | 'step';
  onChoose: (event: React.MouseEvent) => void;
  forwardedRef: Ref<TabsItemRefElement> | null;
  isSelected: boolean;
  type: Props['type'];
};

// Since this is a component rather than a DOM node, `forwardRef`
// is used to facilitate properly passing the `ref` along to the DOM node.
const TabsItem = (props: TabsItemProps) => {
  // Render a `button[disabled]` when `props.disabled` to prevent
  // users from tabbing to it while still providing accessible feedback
  // if users navigate to it in another way. Note that `onClick` and
  // `ref` are also not applied since a disabled `NavItem` shouldn't
  // be able to be clicked (`onClick`) or selected (`ref`).
  if (props.disabled) {
    return (
      <button
        type="button"
        aria-label={props.ariaLabel}
        className={styles.item}
        disabled={true}
        data-selected={props.isSelected ? true : undefined}
        data-zds
      >
        <span className={styles.itemText} data-zds>
          {props.text}
        </span>
        {props.badgeText && (
          <Badge
            className={clsx(
              styles.badgeAndTag,
              props.disabled && styles.badgeDisabled
            )}
            variant={props.badgeVariant}
          >
            {props.badgeText}
          </Badge>
        )}
        {props.tagText && (
          <Tag
            variant={props.tagVariant}
            className={clsx(
              styles.badgeAndTag,
              props.disabled && styles.badgeDisabled
            )}
          >
            {props.tagText}
          </Tag>
        )}
      </button>
    );
  }

  let isSelectedBadgeVariant: BadgeProps['variant'] = props.badgeVariant;
  switch (props.badgeVariant) {
    case 'neutral':
      isSelectedBadgeVariant = 'neutral-stronger';
      break;
    case 'primary':
      isSelectedBadgeVariant = 'primary-stronger';
      break;
    case 'brand':
      isSelectedBadgeVariant = 'brand-stronger';
      break;
    case 'success':
      isSelectedBadgeVariant = 'success-stronger';
      break;
    case 'warning':
      isSelectedBadgeVariant = 'warning-stronger';
      break;
    case 'error':
      isSelectedBadgeVariant = 'error-stronger';
      break;
  }

  return (
    <Link
      aria-label={props.ariaLabel}
      aria-current={props.isSelected ? props.ariaCurrent : undefined}
      color={null}
      className={styles.item}
      data-selected={props.isSelected ? true : undefined}
      href={props.href}
      onClick={props.onChoose}
      ref={props.forwardedRef as Ref<HTMLAnchorElement>}
    >
      <span className={styles.itemText} data-text={props.text} data-zds>
        {props.text}
      </span>
      {props.badgeText && (
        <Badge
          className={clsx(
            styles.badgeAndTag,
            props.disabled && styles.badgeDisabled
          )}
          variant={
            props.isSelected ? isSelectedBadgeVariant : props.badgeVariant
          }
        >
          {props.badgeText}
        </Badge>
      )}
      {props.tagText && (
        <Tag
          variant={props.tagVariant}
          className={clsx(
            styles.badgeAndTag,
            props.disabled && styles.badgeDisabled
          )}
        >
          {props.tagText}
        </Tag>
      )}
    </Link>
  );
};

/**
 * Creates a horizontal tab navigation component that can display a variable number of items, and allows excess items to be nested into a dropdown menu.
 *
 * `Tabs` updates which item is selected based on its `selectedItemHref` prop.
 */
export const Tabs = forwardRef(
  (
    {
      items,
      selectedItemHref,
      'aria-label': ariaLabel = undefined,
      'aria-current': ariaCurrent = 'page',
      isItemSelected = (item: TabsItemType, selectedItemHref: string) =>
        item.href === selectedItemHref,
      onChooseItem = () => {},
      shouldRenderTrack = true,
      size = 'large',
      type = 'default',
    }: Props,
    ref: React.Ref<HTMLDivElement>
  ) => {
    // Hold onto ref so the selected node can be measured
    // in order to position `indicator` via `indicatorStyles`.
    const selectedItemRef = useRef<TabsItemRefElement>(null);
    const [indicatorStyles, setIndicatorStyles] = useState({});

    const shouldRenderIndicator = !items.every((i) => i.isExtra);

    const primaryItems = items.filter((item) => !item.isExtra);
    const primaryLinks = primaryItems.map((item) => {
      const isSelected = isItemSelected(item, selectedItemHref);
      return (
        <TabsItem
          {...item}
          ariaCurrent={ariaCurrent}
          forwardedRef={
            isSelected && shouldRenderIndicator ? selectedItemRef : null
          }
          isSelected={isSelected}
          key={item.href}
          onChoose={(event) => onChooseItem(item, event)}
          type={type}
        />
      );
    });

    const secondaryItems = items.filter((item) => item.isExtra);
    const isSecondaryItemSelected = secondaryItems.some((item) => {
      return isItemSelected(item, selectedItemHref);
    });
    const secondaryMenu = secondaryItems.length > 0 && (
      <div
        ref={
          isSecondaryItemSelected && shouldRenderIndicator
            ? (selectedItemRef as Ref<HTMLDivElement>)
            : null
        }
        data-zds
      >
        <TabsDropdown
          size={size}
          items={secondaryItems}
          onChooseItem={onChooseItem}
          selectedItemHref={selectedItemHref}
        />
      </div>
    );

    const updateIndicatorPosition = () => {
      if (selectedItemRef.current) {
        setIndicatorStyles({
          left: selectedItemRef.current.offsetLeft,
          width: selectedItemRef.current.offsetWidth,
        });
      }
    };

    // When page loads, use requestAnimationFrame to initially position the indicator bar.
    // This is sometimes not perfectly accurate since some styles may still be unapplied.
    // ResizeObserver will watch for changes and ensure that the indicator bar is always aligned.
    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        updateIndicatorPosition();
      });

      if (selectedItemRef.current) {
        resizeObserver.observe(selectedItemRef.current);

        requestAnimationFrame(updateIndicatorPosition);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, [items, selectedItemHref]);

    return (
      <nav
        aria-label={ariaLabel}
        className={styles.tabsRoot}
        data-size={size}
        data-zds
      >
        <div
          className={styles.primaryLinks}
          style={{
            '--_zds-tabs-link-count': primaryLinks.length,
          }}
          ref={ref}
          data-zds
        >
          {primaryLinks}
        </div>
        {secondaryMenu}
        <div
          className={styles.indicatorTrack}
          data-render-track={shouldRenderTrack ? true : undefined}
          data-zds
        >
          <div
            className={styles.indicator}
            data-variant={type}
            style={indicatorStyles}
            data-zds
          />
        </div>
      </nav>
    );
  }
);
